import type { FilterOption } from '../types';

/**
 * What a multi-select's closed trigger says.
 *
 * Pulled out of the component because the interesting case is not a rendering
 * detail: an option list is fetched, so a SELECTED value can be absent from it
 * — a retired employment type, a position nobody holds any more, a saved report
 * naming something since deactivated, or simply options that have not arrived.
 * Summarising the OPTIONS rather than the VALUE made the control render its
 * "no filter" placeholder over a filter that was very much narrowing the
 * report; the count and the placeholder therefore key off `value`, and anything
 * the list cannot name is counted rather than dropped.
 */
export type SelectionSummary =
  /** Nothing selected — show the placeholder. */
  | { kind: 'empty' }
  /** Few enough to name: the options we found, plus how many we could not. */
  | { kind: 'badges'; options: FilterOption[]; unnamed: number }
  /** Too many to name — one badge with the total. */
  | { kind: 'count'; count: number };

export function summarizeSelection(
  options: FilterOption[],
  value: string[],
  maxBadges: number,
): SelectionSummary {
  if (value.length === 0) return { kind: 'empty' };
  if (value.length > maxBadges) return { kind: 'count', count: value.length };

  const byValue = new Map(options.map((option) => [option.value, option]));
  const found: FilterOption[] = [];
  let unnamed = 0;
  for (const item of value) {
    const option = byValue.get(item);
    if (option) found.push(option);
    else unnamed += 1;
  }
  return { kind: 'badges', options: found, unnamed };
}
